/**
 * Document Scanner Service
 * Abstracts native document scanning with edge detection, auto-capture, and perspective correction
 *
 * iOS: Uses @capgo/capacitor-document-scanner (VisionKit-based)
 * Android: Uses Google ML Kit Document Scanner (auto edge detection + auto capture)
 * PWA: Returns null, components fall back to standard camera
 */

import { DocumentScanner as AndroidDocumentScanner } from '@capacitor-mlkit/document-scanner';
import { DocumentScanner as iOSDocumentScanner, ResponseType } from '@capgo/capacitor-document-scanner';
import { Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { isNativeIOS, isNativeAndroid } from '../utils/platform.utils';

export interface ScanResult {
  images: string[];  // base64 encoded images
  count: number;
  cancelled: boolean;
}

export const documentScannerService = {
  /**
   * Check if native document scanning is available
   */
  isAvailable(): boolean {
    return isNativeAndroid() || isNativeIOS();
  },

  /**
   * Scan documents - uses platform-appropriate scanner
   * iOS: VisionKit via @capgo/capacitor-document-scanner
   * Android: ML Kit with auto edge detection and auto capture
   */
  async scanBulk(maxDocuments: number = 100): Promise<ScanResult> {
    // Android: Use ML Kit Document Scanner
    if (isNativeAndroid()) {
      return this.scanWithMLKit(maxDocuments);
    }

    // iOS: Use VisionKit via Capgo plugin
    if (isNativeIOS()) {
      return this.scanWithVisionKit(maxDocuments);
    }

    // PWA: Not supported - components should fall back to camera
    return { images: [], count: 0, cancelled: false };
  },

  /**
   * iOS: Scan with VisionKit via @capgo/capacitor-document-scanner
   * Uses Apple's native document scanner with edge detection
   */
  async scanWithVisionKit(maxDocuments: number): Promise<ScanResult> {
    try {
      console.log(`[DocumentScanner] iOS: Starting VisionKit scan, maxDocuments: ${maxDocuments}...`);

      const result = await iOSDocumentScanner.scanDocument({
        responseType: ResponseType.Base64,
        maxNumDocuments: maxDocuments,
        letUserAdjustCrop: true  // Let user adjust crop - fixes iOS issues where auto-crop returns empty
      });

      console.log('[DocumentScanner] iOS: VisionKit returned:', JSON.stringify({
        status: result.status,
        hasScannedImages: !!result.scannedImages,
        scannedImagesType: typeof result.scannedImages,
        scannedImagesLength: result.scannedImages?.length,
        scannedImagesIsArray: Array.isArray(result.scannedImages),
        firstImageLength: result.scannedImages?.[0]?.length,
        firstImagePrefix: result.scannedImages?.[0]?.substring(0, 50)
      }));

      if (result.status === 'cancel') {
        return { images: [], count: 0, cancelled: true };
      }

      const images = result.scannedImages || [];
      console.log('[DocumentScanner] iOS: VisionKit returned', images.length, 'images');

      // Debug: log each image info
      images.forEach((img, idx) => {
        console.log(`[DocumentScanner] iOS: Image ${idx + 1}: length=${img?.length}, prefix=${img?.substring(0, 30)}`);
      });

      return {
        images,
        count: images.length,
        cancelled: false
      };
    } catch (error: any) {
      if (error?.message?.includes('cancel')) {
        return { images: [], count: 0, cancelled: true };
      }
      console.error('[DocumentScanner] iOS error:', error);
      throw error;
    }
  },

  /**
   * Android: Scan with Google ML Kit Document Scanner
   * This uses Google's native scanner with auto edge detection
   * Using BASE mode (no edit buttons) for simpler UX
   */
  async scanWithMLKit(maxDocuments: number): Promise<ScanResult> {
    try {
      console.log('[DocumentScanner] Android: Checking ML Kit module...');

      // Ensure ML Kit module is available
      const moduleCheck = await AndroidDocumentScanner.isGoogleDocumentScannerModuleAvailable();
      console.log('[DocumentScanner] Android: Module available:', moduleCheck.available);

      if (!moduleCheck.available) {
        console.log('[DocumentScanner] Android: Installing ML Kit module...');
        await AndroidDocumentScanner.installGoogleDocumentScannerModule();

        // Wait for installation
        await new Promise(resolve => setTimeout(resolve, 3000));

        const recheck = await AndroidDocumentScanner.isGoogleDocumentScannerModuleAvailable();
        if (!recheck.available) {
          throw new Error('ML Kit Document Scanner not available. Please update Google Play Services.');
        }
      }

      console.log(`[DocumentScanner] Android: Starting ML Kit scan with BASE mode, pageLimit: ${maxDocuments}...`);

      // Use BASE mode - no edit buttons, simpler UX
      // pageLimit based on caller's request (1 for single, 25 for bulk)
      const result = await AndroidDocumentScanner.scanDocument({
        pageLimit: maxDocuments,
        galleryImportAllowed: false,
        resultFormats: 'JPEG',
        scannerMode: 'BASE'
      });

      console.log('[DocumentScanner] Android: ML Kit returned', result.scannedImages?.length || 0, 'images');

      // ML Kit returns file URIs, need to convert to base64
      const images: string[] = [];
      if (result.scannedImages && result.scannedImages.length > 0) {
        for (const uri of result.scannedImages) {
          const base64 = await this.fileUriToBase64(uri);
          if (base64) {
            images.push(base64);
          }
        }
      }

      return {
        images,
        count: images.length,
        cancelled: images.length === 0 && (!result.scannedImages || result.scannedImages.length === 0)
      };
    } catch (error: any) {
      if (error?.message?.includes('canceled') || error?.message?.includes('cancelled')) {
        return { images: [], count: 0, cancelled: true };
      }
      console.error('[DocumentScanner] Android error:', error);
      throw error;
    }
  },

  /**
   * Convert file URI to base64 (for Android ML Kit results)
   */
  async fileUriToBase64(uri: string): Promise<string | null> {
    try {
      // Try Capacitor Filesystem first
      let filePath = uri;
      if (uri.startsWith('file://')) {
        filePath = uri.replace('file://', '');
      } else if (uri.startsWith('content://')) {
        // Content URIs - use fetch with convertFileSrc
        const webPath = Capacitor.convertFileSrc(uri);
        const response = await fetch(webPath);
        const blob = await response.blob();
        return await this.blobToBase64(blob);
      }

      const result = await Filesystem.readFile({ path: filePath });

      if (typeof result.data === 'string') {
        return result.data.includes(',') ? result.data.split(',')[1] : result.data;
      }

      if (result.data instanceof Blob) {
        return await this.blobToBase64(result.data);
      }

      return null;
    } catch (error) {
      console.error('[DocumentScanner] File conversion error:', error);
      // Fallback: try fetch with convertFileSrc
      try {
        const webPath = Capacitor.convertFileSrc(uri);
        const response = await fetch(webPath);
        const blob = await response.blob();
        return await this.blobToBase64(blob);
      } catch (fetchError) {
        console.error('[DocumentScanner] Fetch fallback failed:', fetchError);
        return null;
      }
    }
  },

  /**
   * Convert Blob to base64
   */
  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]);
      };
      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  },

  /**
   * Single scan (convenience method)
   */
  async scanSingle(): Promise<ScanResult> {
    return this.scanBulk(1);
  },

  /**
   * Convert base64 image to File object for upload queue
   */
  base64ToFile(base64: string, filename: string): File {
    if (!base64 || base64.length < 100) {
      throw new Error(`Invalid base64 data for ${filename}`);
    }

    let base64Data = base64;
    if (base64Data.includes('base64,')) {
      base64Data = base64Data.split('base64,')[1];
    }
    base64Data = base64Data.replace(/\s/g, '');
    while (base64Data.length % 4 !== 0) {
      base64Data += '=';
    }

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });
    const file = new File([blob], filename, { type: 'image/jpeg' });

    if (file.size === 0) {
      throw new Error(`File ${filename} is 0 bytes after conversion`);
    }

    return file;
  },

  /**
   * Resize base64 image to max dimension while maintaining aspect ratio
   */
  async resizeBase64Image(base64: string, maxDimension: number = 1800, quality: number = 0.85): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const resizedBase64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        resolve(resizedBase64);
      };

      img.onerror = () => reject(new Error('Failed to load image for resizing'));
      img.src = base64.includes('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
    });
  }
};
